import { ErrorHandler } from '@wix/error-handler';
import { createHttpClient } from '@wix/fe-essentials/http-client';
import { initI18n } from '@wix/fe-essentials/i18n';
import { ErrorHandlerProvider, ExperimentsProvider, HttpClientProvider, I18nextProvider, PanoramaProvider } from '@wix/fe-essentials/react';
import { QueryClient } from '@wix/fe-essentials/react-query/core';
import { QueryClientProvider } from '@wix/fe-essentials/react-query/react';
import webBiLogger from '@wix/web-bi-logger';
import { createStore, Provider as JotaiProvider, useStore } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { activeTabIdAtom, initialPageIdAtom, multiLingualDataAtom, panelStateAtom, signedInstanceAtom } from '../atoms/session-atoms';
import { PanelLoader } from '../components/wizard-content/panel-loader';
import { ACCESSIBILITY_WIZARD_TAB_IDS } from '../consts';
import { DuplexerProvider } from '../duplexer/use-duplexer-context';
import { A11yWizardAdapterContext } from '../hooks/use-a11y-wizard-adapter';
import { SessionInfoContext } from '../hooks/use-session-info-context';
import { useCreatePanoramaClient } from '../panorama/use-create-panorama-client';
import { dataHooks } from '../utils/datahooks';
import { initializeI18n } from './i18n';
export const A11yWizardProvider = _ref => {
  let {
    locale,
    children,
    signedInstance,
    metaSiteId,
    editorSessionId,
    a11yWizardAdapter,
    multiLingualData,
    experiments,
    referral,
    galleryInstance,
    storesInstance,
    initialData,
    isDebugMode,
    editorHost,
    onPanelStateChange,
    initialPanelState,
    initialPageId,
    duplexerData,
    initDuplexer,
    userPermissions
  } = _ref;
  const initialJotaiStore = useMemo(() => createStore(), []);
  const jotaiStore = useStore({
    store: initialJotaiStore
  });
  const wizardOpenTimestamp = useMemo(() => Date.now(), []);
  const queryClient = useMemo(() => new QueryClient({}), []);
  const panoramaClient = useCreatePanoramaClient({
    esi: editorSessionId,
    msid: metaSiteId
  });
  const [publishedBranchId, setPublishedBranchId] = useState(undefined);
  a11yWizardAdapter.reloadSiteProperties();
  useEffect(() => {
    const fetchPublishedBranchId = async () => {
      const branchId = await a11yWizardAdapter.getPublishedBranchId();
      setPublishedBranchId(branchId);
    };
    fetchPublishedBranchId();
  }, [a11yWizardAdapter, editorHost]);
  const errorHandler = useMemo(() => new ErrorHandler({
    biDefaultsOverrides: {
      app_id: '',
      hostingEnvironment: 'editor'
    },
    biLoggerOrFactory: webBiLogger.factory,
    createErrorMonitor: () => {
      var _panoramaClient$error;
      return {
        captureException: panoramaClient ? panoramaClient == null || panoramaClient.errorMonitor == null || (_panoramaClient$error = panoramaClient.errorMonitor()) == null ? void 0 : _panoramaClient$error.reportError : _error => {}
      };
    },
    createI18n: options => initI18n({
      ...options,
      locale
    }),
    environment: {
      artifactId: 'a11y-wizard',
      fullArtifactId: 'com.wixpress.a11y-wizard'
    }
  }), [locale, panoramaClient]);
  const httpClient = useMemo(() => createHttpClient({
    errorHandler,
    headers: {
      Authorization: signedInstance
    }
  }), [errorHandler, signedInstance]);
  const i18n = useMemo(() => initializeI18n(locale, editorHost), [editorHost, locale]);
  useHydrateAtoms([[panelStateAtom, initialPanelState ?? {
    globalSectionComponentsDataMap: new Map(),
    groupsDataMap: new Map(),
    pagesData: new Map(),
    verticalsDrillInStateUpdate: new Set()
  }], [activeTabIdAtom, ACCESSIBILITY_WIZARD_TAB_IDS.DETECTED_TASKS], [multiLingualDataAtom, multiLingualData], [signedInstanceAtom, signedInstance], [initialPageIdAtom, initialPageId]], {
    store: initialJotaiStore
  });
  useEffect(() => {
    jotaiStore.set(multiLingualDataAtom, multiLingualData);
  }, [multiLingualData, jotaiStore]);
  if (!panoramaClient || publishedBranchId === undefined) {
    return /*#__PURE__*/React.createElement(PanelLoader, null);
  }
  return /*#__PURE__*/React.createElement(Suspense, {
    fallback: null
  }, /*#__PURE__*/React.createElement(JotaiProvider, {
    store: initialJotaiStore
  }, /*#__PURE__*/React.createElement(QueryClientProvider, {
    client: queryClient
  }, /*#__PURE__*/React.createElement(HttpClientProvider, {
    value: httpClient
  }, /*#__PURE__*/React.createElement(ErrorHandlerProvider, {
    value: errorHandler
  }, /*#__PURE__*/React.createElement(ExperimentsProvider, {
    experiments: experiments
  }, /*#__PURE__*/React.createElement(I18nextProvider, {
    i18n: i18n
  }, /*#__PURE__*/React.createElement(PanoramaProvider, {
    value: panoramaClient
  }, /*#__PURE__*/React.createElement(DuplexerProvider, {
    duplexerData: duplexerData,
    initDuplexer: initDuplexer,
    signedInstance: signedInstance
  }, /*#__PURE__*/React.createElement(SessionInfoContext.Provider, {
    value: {
      branchId: a11yWizardAdapter.getCurrentBranchId(),
      esi: editorSessionId,
      galleryInstance,
      initialData,
      isDebugMode,
      locale,
      msid: metaSiteId,
      publishedBranchId,
      referral,
      setPublishedBranchId,
      storesInstance,
      userPermissions,
      wizard_session_start_ts: wizardOpenTimestamp
    }
  }, /*#__PURE__*/React.createElement(A11yWizardAdapterContext.Provider, {
    value: {
      a11yWizardAdapter,
      editorHost,
      onPanelStateChange
    }
  }, /*#__PURE__*/React.createElement("span", {
    "data-testid": dataHooks.a11yWizardProvider
  }, children))))))))))));
};
//# sourceMappingURL=a11y-wizard-provider.js.map